// src/client/game/scenes/SnakeGameScene.ts

import Phaser from "phaser";
import { GameModel, FoodKind } from "../core/model";
import type { Direction, RunStats } from "../core/model";

import { GridLayout } from "../render/GridLayout";
import { GridRenderer } from "../render/GridRenderer";
import { LaserRenderer } from "../render/LaserRenderer";
import { Hud } from "../ui/Hud";
import { LevelCompleteOverlay } from "../ui/LevelCompleteOverlay";
import { getSelectedLevel } from "../core/levels/LevelSelection";
import { clampLevel, createLevel, MAX_LEVEL } from "../core/levels/LevelFactory";
import { LevelRun } from "../core/levels/LevelRun";
import { LEVEL_CONFIGS } from "../core/levels/LevelConfigs";
import { WeightedTable } from "../core/levels/WeightedTable";
import type { FoodKey } from "../core/levels/LevelConfig";
// import { InstructionsOverlay } from "../ui/InstructionsOverlay";
import { LeaderboardOverlay } from "../ui/LeaderboardOverlay";
import { KeyboardControls } from "../input/KeyboardControls";
import { VirtualJoystick } from "../input/VirtualJoystick";
import { VirtualDPad } from "../input/VirtualDPad";
import { VirtualSwipe } from "../input/VirtualSwipe";

import {
  initMobileControlsMode,
  getMobileControlsMode,
} from "../input/mobileControlsSettings";
import { Sfx } from "../audio/Sfx";
import { GameOverOverlay } from "../ui/GameOverOverlay";

const GRID_WIDTH = 26;
const GRID_HEIGHT = 18;

const BG_COLOR = 0x000000;
const GRID_OFFSET_X = 16;
const GRID_OFFSET_Y = 16;
const HUD_RESERVE_PX = 140;

const BASE_SNAKE_SPEED_MS = 500;

const FOOD_KEY_TO_KIND: Record<FoodKey, FoodKind> = {
  RED: FoodKind.Normal,
  GOLD: FoodKind.Gold,
  PURPLE_LASER: FoodKind.Laser,
  BLUE_ICE: FoodKind.Blue,
  STAR_TRIPLE: FoodKind.Star,
  ICY_LIGHTNING: FoodKind.Icy,
};


type GameSpeedSetting = "low" | "med" | "high";

/**
 * Speed scaling via ConfigScene.
 * "med" (or missing) => 1.0 (effectively OFF).
 */
function getSpeedMultiplier(scene: Phaser.Scene): number {
  const setting = scene.registry.get("gameSpeedSetting") as
    | GameSpeedSetting
    | undefined;

  if (setting === "low") return 0.5;
  if (setting === "high") return 1.5;
  return 1.0;
}

type MobileControls = {
  create(): void;
  destroy(): void;
};

export class SnakeGameScene extends Phaser.Scene {
  private model!: GameModel;
  private levelRun!: LevelRun;

  private layout!: GridLayout;
  private hud!: Hud;
  private renderer!: GridRenderer;
  private laser!: LaserRenderer;

  private keyboard!: KeyboardControls;
  private joystick?: MobileControls;

  // private instructions!: InstructionsOverlay;
  private leaderboard!: LeaderboardOverlay;
  private gameOverOverlay!: GameOverOverlay;
  private levelCompleteOverlay!: LevelCompleteOverlay;

  private sfx = new Sfx();

  private submittedScoreThisRun = false;
  private state: "playing" | "gameOver" | "levelComplete" = "playing";

  // Step 19: boss phase hook (level 10)
  private bossSpawned = false;
  private bossPhase: 1 | 2 = 1;

  constructor() {
    super("SnakeGameScene");
  }

  private applyCampaignTuning(levelId: number): void {
    const cfg = LEVEL_CONFIGS.find((c) => c.id === levelId) ?? LEVEL_CONFIGS[0]!;

    // Reset boss state for this level.
    this.bossSpawned = false;
    this.bossPhase = 1;

    // Apply baseline movement speed for this level (gold/blue can still modify speedMultiplier).
    const mul = Math.max(0.1, cfg.difficulty.snakeSpeedMul);
    this.model.setBaseSpeedMs(BASE_SNAKE_SPEED_MS / mul);

    // Food pacing + mix is fully level-driven now.
    const rngLike = { next: () => this.model.rng() };
    // Step 16: gate charge foods by level flags (triple/lightning unlocks).
    const flags = cfg.flags ?? {};
    const allowLasers = !!flags["lasers"];
    const allowTriple = !!flags["triple"];
    const allowLightning = !!flags["lightning"];

    const gatedEntries = cfg.foodWeights.toJSON().filter((e) => {
      if (e.item === "PURPLE_LASER") return allowLasers;
      if (e.item === "STAR_TRIPLE") return allowTriple;
      if (e.item === "ICY_LIGHTNING") return allowLightning;
      return true;
    });

    const gatedFoodWeights = gatedEntries.length
      ? new WeightedTable(gatedEntries)
      : WeightedTable.single<FoodKey>("RED");

    this.model.setFoodSpawnTuning({
      spawnEveryMs: cfg.difficulty.foodSpawnEveryMs,
      maxFoodOnBoard: cfg.difficulty.maxFoodOnBoard,
      pickFoodKind: () => FOOD_KEY_TO_KIND[gatedFoodWeights.pick(rngLike)],
    });

    // Step 17: competitor enemies (scavengers) are enabled by level config.
    if (cfg.enemies.includes("SCAVENGER")) {
      const pressure = Math.max(0, cfg.difficulty.enemyPressure ?? 0);
      // Map pressure into a small starting pack size.
      const count = Math.min(5, Math.max(1, Math.round(pressure * 3)));
      for (let i = 0; i < count; i++) this.model.spawnEnemySnake();
    }

    // Step 18: laser-capable hunters are enabled by level config (late-game).
    if (cfg.enemies.includes("HUNTER")) {
      const pressure = Math.max(0, cfg.difficulty.enemyPressure ?? 0);
      // Keep hunters sparse; they’re much more dangerous than scavengers.
      const count = Math.min(3, Math.max(1, Math.round(pressure * 1.5)));
      for (let i = 0; i < count; i++) this.model.spawnHunterSnake();
    }

    // Step 19: boss spawns in boss levels (level 10) and phases up near the end.
    if (cfg.enemies.includes("BOSS")) {
      this.model.spawnBossSnake();
      this.bossSpawned = true;
      this.bossPhase = 1;
      this.model.setBossPhase(1);
    }
  }

  create(): void {
    this.model = new GameModel(GRID_WIDTH, GRID_HEIGHT);

    // Levels: read the user's selected starting level and anchor per-level progress.
    const selectedLevel = getSelectedLevel();
    this.levelRun = new LevelRun(createLevel(selectedLevel));
    this.levelRun.start(this.model.score);

    // Step 15: apply level-driven spawn + difficulty tuning.
    this.applyCampaignTuning(this.levelRun.level.id);
    this.model.resetFoods();

    this.cameras.main.setBackgroundColor(BG_COLOR);

    this.layout = new GridLayout(
      GRID_WIDTH,
      GRID_HEIGHT,
      GRID_OFFSET_X,
      GRID_OFFSET_Y,
      HUD_RESERVE_PX
    );
    this.layout.recomputeLayout(this.scale.width, this.scale.height);

    this.hud = new Hud(this, this.layout);
    this.hud.createStaticUI();

    // Keep the board centered and HUD below it when the viewport changes.
    this.scale.on(Phaser.Scale.Events.RESIZE, () => {
      this.layout.recomputeLayout(this.scale.width, this.scale.height);
      this.renderer.syncFromModel(this.model, true);
      this.hud.relayout();
    });

    this.renderer = new GridRenderer(this, this.layout);
    this.laser = new LaserRenderer(this, this.layout);

    // Unlock audio on first real gesture (WebViews often require this)
    this.input.once("pointerdown", () => this.sfx.ensure());
    this.input.keyboard?.once("keydown", () => this.sfx.ensure());

    // No InstructionsOverlay anymore – start immediately in playing state.

    this.leaderboard = new LeaderboardOverlay(
      this,
      () => this.getRunStats(),
      () => this.restartGame()
    );

    this.gameOverOverlay = new GameOverOverlay(
      this,
      () => this.getRunStats(),
      (score: number) => this.computeRankPosition(score),
      {
        onPlayAgain: () => this.restartGame(),
        onMainMenu: () => this.scene.start("MenuScene"),
      }
    );

    this.levelCompleteOverlay = new LevelCompleteOverlay(this, {
      onNextLevel: () => this.advanceToNextLevel(),
      onMainMenu: () => this.scene.start("MenuScene"),
    });

    this.keyboard = new KeyboardControls(
      this,
      () => this.state === "playing",
      (dir: Direction) => this.model.setDirection(dir)
    );

    // Decide which mobile controls to use: joystick, D-pad, or swipe
    initMobileControlsMode(this);
    const controlsMode = getMobileControlsMode(this);

    if (controlsMode === "joystick") {
      this.joystick = new VirtualJoystick(
        this,
        () => this.state === "playing",
        () => this.state === "playing" && this.model.hasLaser,
        (dir: Direction) => this.model.setDirection(dir),
        () => this.tryFireLaser()
      );
    } else if (controlsMode === "swipe") {
      this.joystick = new VirtualSwipe(
        this,
        () => this.state === "playing",
        () => this.state === "playing" && this.model.hasLaser,
        (dir: Direction) => this.model.setDirection(dir),
        () => this.tryFireLaser()
      );
    } else {
      // default: D-pad
      this.joystick = new VirtualDPad(
        this,
        () => this.state === "playing",
        () => this.state === "playing" && this.model.hasLaser,
        (dir: Direction) => this.model.setDirection(dir),
        () => this.tryFireLaser()
      );
    }

    this.joystick.create();

    this.renderer.syncFromModel(this.model, true);
    this.updateHudFromModel();

    this.events.on(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown, this);
    this.events.on(Phaser.Scenes.Events.DESTROY, this.onShutdown, this);

    // Immediately start the game.
    this.state = "playing";
    this.submittedScoreThisRun = false;
  }

  private onShutdown(): void {
    this.keyboard?.destroy();
    this.leaderboard?.clear();
    this.gameOverOverlay?.clear();
    this.levelCompleteOverlay?.clear();
    this.joystick?.destroy();
  }

  update(_time: number, delta: number): void {
    const scaledDelta = delta * getSpeedMultiplier(this);

    this.laser.update(scaledDelta);

    if (this.state === "gameOver") {
      // Keep showing final meta while in game over state
      this.hud.setMeta(this.model.timeAliveMs, this.model.laserLevel);
      this.hud.setStatus("Game over");

      // Optional: still allow quick restart via SPACE
      if (this.keyboard.wasSpaceJustDown()) {
        this.sfx.ensure();
        this.restartGame();
      }
      return;
    }

    if (this.state === "levelComplete") {
      // Keep HUD meta alive while paused.
      this.hud.setMeta(this.model.timeAliveMs, this.model.laserLevel);
      this.hud.setStatus("Level complete");

      // Convenient: SPACE advances (same as clicking Next Level).
      if (this.keyboard.wasSpaceJustDown()) {
        this.sfx.ensure();
        this.advanceToNextLevel();
      }
      return;
    }

    // Normal playing state
    const result = this.model.tick(scaledDelta);

    // Enemy lasers (hunters) can fire on the same tick the player moves.
    if (this.model.enemyLaserShots.length > 0) {
      for (const shot of this.model.enemyLaserShots) {
        this.laser.drawEnemyLaserBeam(shot.origin, shot.path);
      }
    }

    // Update meta every frame in play (so the timer updates smoothly)
    this.hud.setMeta(this.model.timeAliveMs, this.model.laserLevel);

    // Step 19: boss phase hook — enrages at 80% level progress.
    if (this.bossSpawned && this.levelRun.level.id === 10 && this.bossPhase === 1) {
      const pct = this.levelRun.percent(this.model.score);
      if (pct >= 0.8) {
        this.bossPhase = 2;
        this.model.setBossPhase(2);
      }
    }

    if (result.died) {
      this.sfx.gameOver();
      this.state = "gameOver";
      this.renderer.syncFromModel(this.model, true);
      this.updateHudFromModel();

      // Submit score before showing Game Over overlay
      if (!this.submittedScoreThisRun) {
        this.submittedScoreThisRun = true;
        this.leaderboard.submitScoreOnly(this.model.score);
      }

      this.gameOverOverlay.show();
      return;
    }

    if (result.ateFoods.length > 0) {
      for (const food of result.ateFoods) {
        this.sfx.eatFood(food.id);
      }
    }

    if (result.moved) {
      this.sfx.crawl(this.model.score);
      this.renderer.syncFromModel(this.model, false);
      this.updateHudFromModel();
    }

    // Level completion: reach target score (1000) for the current level.
    if (result.moved && this.levelRun.isComplete(this.model.score)) {
      this.onLevelComplete();
      return;
    }

    // While playing, SPACE is only for firing the laser
    if (this.model.hasLaser && this.keyboard.wasSpaceJustDown()) {
      this.tryFireLaser();
    }
  }

  private onLevelComplete(): void {
    this.state = "levelComplete";

    const current = this.levelRun.level;
    const next =
      current.id >= MAX_LEVEL ? null : createLevel(clampLevel(current.id + 1));

    this.levelCompleteOverlay.show({ current, next });
    this.updateHudFromModel();
  }

  private advanceToNextLevel(): void {
    const current = this.levelRun.level;
    if (current.id >= MAX_LEVEL) {
      this.scene.start("MenuScene");
      return;
    }

    // Reset the board but keep run-wide stats (score/time/kills).
    const savedScore = this.model.score;
    const savedTime = this.model.timeAliveMs;
    const savedKills = this.model.enemiesKilled ?? 0;

    this.model.reset();
    this.model.snake.score = savedScore;
    this.model.timeAliveMs = savedTime;
    this.model.enemiesKilled = savedKills;

    const nextLevelId = clampLevel(current.id + 1);
    this.levelRun = new LevelRun(createLevel(nextLevelId));
    this.levelRun.start(this.model.score);

    this.applyCampaignTuning(nextLevelId);
    this.model.resetFoods();

    this.state = "playing";
    this.levelCompleteOverlay.hide();

    this.renderer.clearDynamicSprites();
    this.renderer.syncFromModel(this.model, true);
    this.updateHudFromModel();
  }

  private restartGame(): void {
    this.model.reset();
    this.levelRun.start(this.model.score);

    this.applyCampaignTuning(this.levelRun.level.id);
    this.model.resetFoods();
    this.state = "playing";
    this.submittedScoreThisRun = false;

    // 🧹 clear old graphics and overlays
    this.renderer.clearDynamicSprites();
    this.leaderboard.clear();
    this.gameOverOverlay.hide();

    // 🧩 Recompute layout (GridLayout auto-centers horizontally + reserves HUD space)
    this.layout.recomputeLayout(this.scale.width, this.scale.height);
    this.hud.relayout();

    // 🔄 Fully refresh renderer and HUD
    this.renderer.syncFromModel(this.model, true);
    this.updateHudFromModel();
  }

  private updateHudFromModel(): void {
    this.hud.setScore(this.model.score);
    this.hud.setMeta(this.model.timeAliveMs, this.model.laserLevel);

    const lvlScore = this.levelRun.score(this.model.score);
    this.hud.setLevelProgress(this.levelRun.level.id, this.levelRun.level.label, lvlScore, this.levelRun.level.targetScore);

    if (this.model.isGameOver) {
      this.hud.setStatus("Game over");
      return;
    }

    this.hud.setStatus(
      this.model.hasLaser
        ? "SPACE fires red laser."
        : "Collect purple for laser."
    );
  }

  private tryFireLaser(): void {
    const used = this.model.fire();
    if (used) {
      this.sfx.laserPew();

      this.renderer.syncFromModel(this.model, true);
      this.updateHudFromModel();
      this.laser.drawLaserBeam(this.model, GRID_WIDTH, GRID_HEIGHT);
    }
  }

  private getRunStats(): RunStats {
    return {
      score: this.model.score,
      timeMs: this.model.timeAliveMs,
      laserLevel: this.model.laserLevel,
    };
  }

  private computeRankPosition(score: number): { rank: number; total: number } {
    const leaderboardData = this.leaderboard.getEntries?.() ?? [];
    const sorted = leaderboardData.slice().sort((a, b) => b.score - a.score);
    const total = sorted.length;
    const rank =
      total === 0
        ? 0
        : sorted.findIndex((entry) => entry.score <= score) + 1 || total;
    return { rank, total };
  }

  private showLeaderboard(): void {
    // Only submit once per run; after that, just show again if needed.
    if (!this.submittedScoreThisRun) {
      this.submittedScoreThisRun = true;
    }
    this.leaderboard.submitAndShow();
  }

  private shareFinalMoment(): void {
    const stats = this.getRunStats();
    const title = `My LaserSnake final moment – Score ${stats.score}`;
    const seconds = Math.floor(stats.timeMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const leftoverSec = seconds % 60;
    const timeStr = minutes > 0 ? `${minutes}m ${leftoverSec}s` : `${leftoverSec}s`;

    const textLines = [
      `Score: ${stats.score}`,
      `Time Alive: ${timeStr}`,
      `Laser Level: ${stats.laserLevel}`,
      "",
      "Describe the chaos of your final moment here and attach a screenshot!",
    ];
    const text = textLines.join("\n");

    const base = "https://www.reddit.com/r/LaserSnake/submit";
    const url =
      base +
      "?title=" +
      encodeURIComponent(title) +
      "&text=" +
      encodeURIComponent(text);

    // Open reddit submission page in a new tab/window.
    // NOTE: This should be triggered from the button's pointer event,
    // which it is, to avoid popup blockers in most browsers.
    // eslint-disable-next-line no-undef
    if (typeof window !== "undefined" && window.open) {
      window.open(url, "_blank");
    }
  }
}